import { Alert, Box, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import pluralize from 'pluralize';
import React from 'react';

import { BulkSlideTagSummary } from 'api/slideTags';
import { LinkToSlideViewerComponent } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import { SlidePreviewPanel } from 'components/atoms/SlidePreviewPanel';
import { filter, find, isEmpty, map } from 'es-toolkit/compat';
import { Slide } from 'interfaces/slide';
import { SlideTag } from 'interfaces/slideTag';
import { previewTagScopeLabels } from './constants';

interface BulkSlideTagSummaryReviewProps {
  summary?: BulkSlideTagSummary;
  selectedTags: SlideTag[];
  hydratedSlidesById: Record<string, Slide>;
  previewSlideIds: string[];
  selectedSlideCount: number;
  previewLimit: number;
  isLoadingExpectedResults?: boolean;
  studyId?: string;
}

const summaryMetrics = (summary: BulkSlideTagSummary) => [
  ['Selected Slides', summary.targetSlideCount],
  ['Slides with changes', summary.changedSlideCount],
  ['Slides without changes', summary.unchangedSlideCount],
];

export const BulkSlideTagSummaryReview: React.FC<BulkSlideTagSummaryReviewProps> = ({
  summary,
  selectedTags,
  hydratedSlidesById,
  previewSlideIds,
  selectedSlideCount,
  previewLimit,
  isLoadingExpectedResults = false,
  studyId,
}) => {
  const [selectedPreviewSlideId, setSelectedPreviewSlideId] = React.useState(previewSlideIds[0]);
  const selectedPreviewSlide =
    find(summary?.previewSlides || [], { slideId: selectedPreviewSlideId }) || summary?.previewSlides?.[0];
  const previewSlides = React.useMemo(
    () =>
      map(previewSlideIds, (slideId) => ({
        ...hydratedSlidesById[slideId],
        id: slideId,
      })).filter((slide) => Boolean(slide.id)),
    [hydratedSlidesById, previewSlideIds]
  );
  const selectedResolvedSlide = find(previewSlides, { id: selectedPreviewSlideId }) || previewSlides[0];
  const selectedResolvedSlideAssignments = selectedResolvedSlide?.slideTagAssignments || [];
  const optimisticPreviewTags = React.useMemo(
    () =>
      map(selectedResolvedSlideAssignments, (assignment) => {
        const matchedTag = find(selectedTags, { id: assignment.tagId });
        const scope = assignment.orchestrationId ? 'experiment_result' : assignment.studyId ? 'study' : 'global';
        return {
          key: `${scope}-${assignment.tagId}-${assignment.studyId || ''}-${assignment.orchestrationId || ''}`,
          label: `${matchedTag?.tagValue || assignment.tagId} (${previewTagScopeLabels[scope] || scope})`,
        };
      }),
    [selectedResolvedSlideAssignments, selectedTags]
  );
  const metrics = summary
    ? summaryMetrics(summary)
    : [
        ['Targets', selectedSlideCount],
        ['Preview', previewSlides.length],
      ];

  React.useEffect(() => {
    setSelectedPreviewSlideId(previewSlideIds[0]);
  }, [previewSlideIds]);

  return (
    <Stack spacing={2}>
      <Grid container spacing={1}>
        {map(
          filter(metrics, ([_, value]) => Boolean(value)),
          ([label, value]) => (
            <Grid item xs={6} sm={3} md={2} key={label}>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, minHeight: 64 }}>
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="h5">{value}</Typography>
              </Box>
            </Grid>
          )
        )}
      </Grid>

      {isLoadingExpectedResults && (
        <Alert severity="info" icon={<CircularProgress size={16} color="inherit" />}>
          Loading expected results from backend. Preview currently shows existing slide tags.
        </Alert>
      )}

      {summary?.ineligibleSlideCount ? (
        <Alert severity="warning">
          {summary.ineligibleSlideCount} {pluralize('slide', summary.ineligibleSlideCount)} will be excluded because no
          orchestration can be resolved for the selected result scope.
        </Alert>
      ) : null}

      {summary?.removedAssignmentCount ? (
        <Alert severity="info">
          Clear removes assignments across global, study, and experiment-result scopes. Current removal estimate:{' '}
          {summary.removedAssignmentsByScope.globalCount} global, {summary.removedAssignmentsByScope.studyCount} study,{' '}
          {summary.removedAssignmentsByScope.experimentResultCount} result-scoped.
        </Alert>
      ) : null}

      {!isEmpty(summary?.countsByTag) && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {map(summary?.countsByTag || [], (count) => {
            const tag = find(selectedTags, { id: count.tagId });
            return (
              <Chip
                key={count.tagId}
                label={`${tag?.tagValue || count.tagId}: +${count.added} / -${count.removed} / kept ${count.retained}`}
                size="small"
              />
            );
          })}
        </Stack>
      )}

      {!isEmpty(previewSlides) && selectedResolvedSlide && (
        <Stack spacing={1}>
          <SlidePreviewPanel
            slides={previewSlides}
            selectedSlideId={selectedPreviewSlideId}
            onSelectSlide={setSelectedPreviewSlideId}
            previewMinHeight={280}
            detailsContent={
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
                  {summary ? 'Expected tags' : 'Current tags'}
                </Typography>
                {summary ? (
                  isEmpty(selectedPreviewSlide?.expectedSlideTags) ? (
                    <Typography variant="body2" color="text.secondary">
                      No slide tags
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {map(selectedPreviewSlide?.expectedSlideTags || [], (tag) => (
                        <Chip
                          key={`${tag.scope}-${tag.tagId}-${tag.studyId || ''}-${tag.orchestrationId || ''}`}
                          label={`${tag.tagValue || tag.tagId} (${previewTagScopeLabels[tag.scope] || tag.scope})`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  )
                ) : isEmpty(optimisticPreviewTags) ? (
                  <Typography variant="body2" color="text.secondary">
                    No slide tags
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {map(optimisticPreviewTags, (tag) => (
                      <Chip key={tag.key} label={tag.label} size="small" variant="outlined" />
                    ))}
                  </Stack>
                )}
                {selectedPreviewSlide?.ineligibleReason && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                    {selectedPreviewSlide.ineligibleReason}
                  </Typography>
                )}
                {selectedResolvedSlide?.id && (
                  <Box sx={{ mt: 1.25 }}>
                    <LinkToSlideViewerComponent
                      slideId={selectedResolvedSlide.id}
                      caseStudyId={studyId}
                      labId={selectedResolvedSlide.labId}
                      selectedSlideIds={[selectedResolvedSlide.id]}
                      viewersStainPreferences={
                        selectedResolvedSlide.stainingType ? [selectedResolvedSlide.stainingType] : undefined
                      }
                      formattedValue="Open slide in viewer"
                      target="_blank"
                      rel="noopener"
                      asButton
                    />
                  </Box>
                )}
              </Box>
            }
          />
          {selectedSlideCount > previewLimit && (
            <Typography variant="caption" color="text.secondary">
              Showing {previewSlides.length} of {selectedSlideCount} target slides.
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};
